import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { makeT } from "@/lib/i18n";

const StudioCtx = createContext(null);

export function StudioProvider({ children }) {
  const [channels, setChannels] = useState([]);
  const [selectedChannelId, setSelectedChannelId] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadChannels = useCallback(async () => {
    const { data } = await api.get("/channels");
    setChannels(data);
    // FIX 4 — default context is Workspace / All channels. NEVER auto-select the first
    // channel; creating a channel must not make it globally active. Only keep a prior
    // explicit selection if it still exists; otherwise stay in workspace scope (null).
    setSelectedChannelId((prev) => (prev && data.some((c) => c.id === prev) ? prev : null));
    return data;
  }, []);

  const loadSettings = useCallback(async () => {
    const { data } = await api.get("/settings");
    setSettings(data);
    return data;
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await Promise.all([loadChannels(), loadSettings()]);
      } finally {
        setLoading(false);
      }
    })();
  }, [loadChannels, loadSettings]);

  const lang = settings?.interface_language || "en";
  const t = useMemo(() => makeT(lang), [lang]);
  const selectedChannel = channels.find((c) => c.id === selectedChannelId) || null;

  return (
    <StudioCtx.Provider
      value={{
        channels, setChannels, loadChannels,
        selectedChannelId, setSelectedChannelId, selectedChannel,
        settings, setSettings, loadSettings,
        lang, t, loading,
      }}
    >
      {children}
    </StudioCtx.Provider>
  );
}

export const useStudio = () => useContext(StudioCtx);
